import { useCallback, useState } from 'react';

import { createNexusApi } from '../../../../domain/nexus/api';
import type {
  NexusBugDetail,
  NexusDeliveryOverview,
  NexusExportScope,
  NexusProjectDetail,
} from '../../../../domain/nexus/model';
import { firebaseNexusGateway } from '../../../../platform/firebase/nexusGateway';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { downloadCsv } from './downloadCsv';
import { diagnosticMessage } from './helpers';

const nexusApi = createNexusApi(firebaseNexusGateway);

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return diagnosticMessage(error);
}

export interface DrillDownViewModel<T> {
  busy: boolean;
  error: string | null;
  data: T | null;
  initialize: () => Promise<void>;
  clearError: () => void;
}

/** Shared loader: one fetch per drill-down level, re-run when the view remounts. */
function useDrillDownLoader<T>(load: () => Promise<T>, context: string, metadata: Record<string, unknown> = {}): DrillDownViewModel<T> {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await load());
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: `GoogleNexusTool.${context}`, metadata });
    } finally {
      setBusy(false);
    }
    // The view remounts per bug/project, so the loader is fixed for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { busy, error, data, initialize, clearError: useCallback(() => setError(null), []) };
}

export function useDeliveryOverviewViewModel(): DrillDownViewModel<NexusDeliveryOverview> {
  return useDrillDownLoader(() => nexusApi.fetchDeliveryOverview(), 'deliveryOverview');
}

export function useBugDetailViewModel(bugId: string): DrillDownViewModel<NexusBugDetail> {
  return useDrillDownLoader(() => nexusApi.fetchDeliveryBug(bugId), 'deliveryBug', { bugId });
}

export function useProjectDetailViewModel(projectId: string): DrillDownViewModel<NexusProjectDetail> {
  return useDrillDownLoader(() => nexusApi.fetchDeliveryProject(projectId), 'deliveryProject', { projectId });
}

/** Builds a CSV on the server for one level and saves it in the browser. */
export function useCsvExport(scope: NexusExportScope, id?: string) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const result = await nexusApi.exportDeliveredTasks(scope, id);
      downloadCsv(result.filename, result.csv);
    } catch (nextError) {
      setError(errorMessage(nextError));
      logGlobalError(nextError, { context: 'GoogleNexusTool.exportDeliveredTasks', metadata: { scope, id } });
    } finally {
      setExporting(false);
    }
  }, [scope, id]);

  return { exporting, error, exportCsv, clearError: useCallback(() => setError(null), []) };
}
